/**
 * Consistent Date Formatting Utility
 * Provides standardized date/time formatting across the application
 */

/**
 * Format options for different use cases
 */
const FORMAT_OPTIONS = {
  // Full date and time: "Dec 3, 2025, 2:30 PM"
  full: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  },
  // Date only: "Dec 3, 2025"
  date: {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  },
  // Short date: "12/3/25"
  shortDate: {
    year: '2-digit',
    month: 'numeric',
    day: 'numeric'
  },
  // Time only: "2:30 PM"
  time: {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  },
  // Relative date for tables: "Dec 3, 2:30 PM" (current year) or "Dec 3, 2024, 2:30 PM" (other years)
  table: {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  },
  // ISO format for APIs: "2025-12-03"
  iso: null // handled specially
}

/**
 * Format a date/timestamp consistently across the application
 * @param {string|Date|number} dateInput - Date string, Date object, or timestamp
 * @param {string} format - One of: 'full', 'date', 'shortDate', 'time', 'table', 'iso', 'relative'
 * @param {string} fallback - Value to return if date is invalid
 * @returns {string} Formatted date string
 */
export function formatDate(dateInput, format = 'full', fallback = '—') {
  if (!dateInput) return fallback

  try {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput)
    
    // Check for invalid date
    if (isNaN(date.getTime())) return fallback

    // Handle ISO format
    if (format === 'iso') {
      return date.toISOString().split('T')[0]
    }

    // Handle relative format
    if (format === 'relative') {
      return formatRelative(date)
    }

    // Handle table format - include year only if not current year
    if (format === 'table') {
      const currentYear = new Date().getFullYear()
      const dateYear = date.getFullYear()
      const options = { ...FORMAT_OPTIONS.table }
      if (dateYear !== currentYear) {
        options.year = 'numeric'
      }
      return new Intl.DateTimeFormat('en-US', options).format(date)
    }

    const options = FORMAT_OPTIONS[format]
    if (!options) {
      console.warn(`Unknown date format: ${format}, using 'full'`)
      return new Intl.DateTimeFormat('en-US', FORMAT_OPTIONS.full).format(date)
    }

    return new Intl.DateTimeFormat('en-US', options).format(date)
  } catch (e) {
    console.warn('Date formatting error:', e)
    return fallback
  }
}

/**
 * Format a date relative to now (e.g., "2 hours ago", "3 days ago")
 * @param {Date} date - Date object
 * @returns {string} Relative time string
 */
function formatRelative(date) {
  const now = new Date()
  const diffMs = now - date
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)

  if (diffSecs < 60) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`
  if (diffMonths < 12) return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`
  
  // For older dates, show the actual date
  return formatDate(date, 'date')
}

/**
 * Format a date range (e.g., "Dec 1 - Dec 31, 2025")
 * @param {string|Date} start - Start date
 * @param {string|Date} end - End date
 * @param {string} fallback - Value to return if dates are invalid
 * @returns {string} Formatted date range
 */
export function formatDateRange(start, end, fallback = '—') {
  if (!start || !end) return fallback
  
  try {
    const startDate = start instanceof Date ? start : new Date(start)
    const endDate = end instanceof Date ? end : new Date(end)
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return fallback

    const sameYear = startDate.getFullYear() === endDate.getFullYear()
    const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth()

    if (sameMonth) {
      // Same month: "Dec 1-31, 2025"
      return `${startDate.toLocaleDateString('en-US', { month: 'short' })} ${startDate.getDate()}-${endDate.getDate()}, ${endDate.getFullYear()}`
    } else if (sameYear) {
      // Same year: "Dec 1 - Jan 15, 2025"
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${endDate.getFullYear()}`
    } else {
      // Different years: "Dec 1, 2024 - Jan 15, 2025"
      return `${formatDate(startDate, 'date')} - ${formatDate(endDate, 'date')}`
    }
  } catch (e) {
    return fallback
  }
}

/**
 * Legacy compatibility: simple date formatter matching existing fmt() functions
 * @param {string|Date} dateInput - Date to format
 * @returns {string} Formatted date or empty string
 */
export function fmt(dateInput) {
  return formatDate(dateInput, 'full', '')
}

export default formatDate
